import React, { useEffect, useMemo } from 'react';
import './App.css';
import "antd/dist/antd.css";
import { AuthenticateDocument } from './AuthenticateDocument';
import { useState } from 'react';
import MainLayout from './components/MainLayout';
import External from './components/ExternalUser';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setConflictPreviewVerionstate, setIsReadOnly, setIsTemplateDocument, setDocumentInformation } from './app/EditorSlice';
import { hasCachedDocument } from './utils/documentCache';
import { getLocalDocument } from './utils/localDocuments';

function App({ isExternal = false, isTemplateDocument = false }) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const conflictPreviewVerion = useSelector((state) => state.editor.conflictPreviewVerion);

  // Local (offline-created) documents live only in localStorage and have no server
  // counterpart, so they skip all auth/fetch APIs. Identified by the ?local=true param.
  const isLocalDocument = useMemo(
    () => new URLSearchParams(document.location.search).get('local') === 'true',
    []
  );

  // Non-mutating check (safe during render): a renderable cache entry lets us
  // skip the auth-gating loader and render the layout immediately.
  const isDocumentCached = useMemo(
    () => !isTemplateDocument && !conflictPreviewVerion && hasCachedDocument(id),
    [id, isTemplateDocument, conflictPreviewVerion]
  );

  useEffect(() => {
    dispatch(setIsTemplateDocument(isTemplateDocument));

    const searchParams = new URLSearchParams(document.location.search)
    const previewVerion = searchParams.get("versionType");
    if (previewVerion) {
      dispatch(setConflictPreviewVerionstate(previewVerion));
      dispatch(setIsReadOnly(true));
    }
  }, []);

  // Seed document info for local docs so the Header renders immediately without an
  // auth call. Owner-level rights (creator is the current user, no members).
  useEffect(() => {
    if (isLocalDocument) {
      const record = getLocalDocument(id);
      if (record) {
        dispatch(setDocumentInformation({
          id: record.id,
          name: record.name,
          creator: record.creator,
          createBy: record.creator?.id,
          members: [],
          privacyId: 1,
          rightType: 2,
          isLocal: true,
        }));
      }
    }
  }, [isLocalDocument, id, dispatch]);


  if (isExternal) {
    return <External />;
  }
  // Local documents: no auth gate, no fetch — render the editor straight away.
  if (isLocalDocument) {
    return <MainLayout />;
  }
  else {
    // Cache hit: render the layout instantly and run auth in the background
    // (no gating loader). Background auth seeds documentInfo from cache, then
    // refreshes it and handles redirects.
    if (isDocumentCached) {
      return (
        <>
          <AuthenticateDocument setIsLoading={setIsAuthenticating} background />
          <MainLayout />
        </>
      );
    }
    if (isAuthenticating && !conflictPreviewVerion) { // isAuthenticating
      return <AuthenticateDocument setIsLoading={setIsAuthenticating} />
    }
    else {
      return <MainLayout />;
    }
  }

}

export default App;
